import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Chip,
  TextField,
} from "@mui/material";
import Plot from "react-plotly.js";
import type {
  Layout as PlotLayout,
  Config as PlotConfig,
  PlotData as PlotlyData,
  PlotlyHTMLElement,
} from "plotly.js";
import { DVHData } from "./dvhTypes";
import { getStructureColor } from "./dvhUtils";

interface DVHChartProps {
  dvhData?: DVHData;
  planAData?: DVHData;
  planBData?: DVHData;
  selectedStructures?: string[];
  onStructuresSelect?: (structures: string[]) => void;
  showPlanFilters?: boolean;
}

const DVHChart: React.FC<DVHChartProps> = ({
  dvhData,
  planAData,
  planBData,
  selectedStructures,
  onStructuresSelect,
  showPlanFilters = false,
}) => {
  const [selectedPlanFilter, setSelectedPlanFilter] = useState("showAll");

  // Determine which data to use - multi-plan mode or single plan mode
  const isMultiPlanMode = !!(planAData && planBData);
  const currentDvhData = dvhData || planAData || planBData;

  // Get all available structure names
  const availableStructures = useMemo(() => {
    if (!currentDvhData) return [];
    return currentDvhData.structures.map(
      (structure: DVHData["structures"][number]) => structure.name,
    );
  }, [currentDvhData]);

  // Initialize internal selected structures state - default to all selected
  const [internalSelectedStructures, setInternalSelectedStructures] = useState<
    string[]
  >(() => {
    return availableStructures;
  });

  // Update internal state when available structures change
  React.useEffect(() => {
    if (
      availableStructures.length > 0 &&
      internalSelectedStructures.length === 0
    ) {
      setInternalSelectedStructures(availableStructures);
    }
  }, [availableStructures, internalSelectedStructures.length]);

  // Use external or internal state for selected structures
  const currentSelectedStructures =
    selectedStructures || internalSelectedStructures;

  const handleStructuresChange = (structures: string[]) => {
    if (onStructuresSelect) {
      onStructuresSelect(structures);
    } else {
      setInternalSelectedStructures(structures);
    }
  };

  // Prepare Plotly data
  const plotData = useMemo<Partial<PlotlyData>[]>(() => {
    const traces: Partial<PlotlyData>[] = [];

    if (isMultiPlanMode && planAData && planBData) {
      // Multi-plan comparison mode
      const getDataForPlan = (plan: "A" | "B") =>
        plan === "A" ? planAData : planBData;
      const getPlansToShow = () => {
        if (selectedPlanFilter === "planA") return ["A"];
        if (selectedPlanFilter === "planB") return ["B"];
        return ["A", "B"]; // showAll
      };

      getPlansToShow().forEach((planId) => {
        const planData = getDataForPlan(planId as "A" | "B");
        const planLabel = planId === "A" ? "Plan A" : "Plan B";
        const lineStyle = planId === "A" ? "solid" : "dash";

        // Show selected structures for the selected plan(s)
        planData.structures.forEach(
          (structure: DVHData["structures"][number]) => {
            if (currentSelectedStructures.includes(structure.name)) {
              const structureColor = getStructureColor(structure.name);

              traces.push({
                x: structure.dvhData.map(
                  (point: DVHData["structures"][number]["dvhData"][number]) =>
                    point.dose,
                ),
                y: structure.dvhData.map(
                  (point: DVHData["structures"][number]["dvhData"][number]) =>
                    point.ratioTotalStructureVolume,
                ),
                type: "scatter",
                mode: "lines",
                name: `${structure.name} (${planLabel})`,
                line: {
                  color: structureColor,
                  width: 3,
                  dash: lineStyle,
                },
                hovertemplate:
                  "<b>%{fullData.name}</b><br>" +
                  "Dose: %{x} cGy<br>" +
                  "Volume: %{y:.1f}%<br>" +
                  "<extra></extra>",
              });
            }
          },
        );
      });
    } else {
      // Single plan mode
      // Show selected structures
      currentDvhData?.structures.forEach(
        (structure: DVHData["structures"][number]) => {
          if (currentSelectedStructures.includes(structure.name)) {
            const structureColor = getStructureColor(structure.name);

            traces.push({
              x: structure.dvhData.map(
                (point: DVHData["structures"][number]["dvhData"][number]) =>
                  point.dose,
              ),
              y: structure.dvhData.map(
                (point: DVHData["structures"][number]["dvhData"][number]) =>
                  point.ratioTotalStructureVolume,
              ),
              type: "scatter",
              mode: "lines",
              name: structure.name,
              line: {
                color: structureColor,
                width: 3,
              },
              hovertemplate:
                "<b>%{fullData.name}</b><br>" +
                "Dose: %{x} cGy<br>" +
                "Volume: %{y:.1f}%<br>" +
                "<extra></extra>",
            });
          }
        },
      );
    }

    return traces;
  }, [
    currentDvhData,
    currentSelectedStructures,
    isMultiPlanMode,
    planAData,
    planBData,
    selectedPlanFilter,
  ]);

  // Plotly layout configuration
  const plotLayout: Partial<PlotLayout> = {
    xaxis: {
      title: { text: "Dose (cGy)" },
      showgrid: true,
      gridcolor: "rgba(128,128,128,0.3)",
      zeroline: false,
      showticklabels: true,
      rangemode: "normal",
      range: [1, null], // Start from 50 cGy instead of 0
      tickformat: ",d",
    },
    yaxis: {
      title: { text: "Volume (%)" },
      range: [0, 100],
      showgrid: true,
      gridcolor: "rgba(128,128,128,0.3)",
      zeroline: true,
      zerolinecolor: "rgba(128,128,128,0.5)",
      zerolinewidth: 1,
    },
    showlegend: currentSelectedStructures.length > 0,
    legend: {
      orientation: "h",
      x: 0,
      y: -0.18,
      xanchor: "left",
      yanchor: "top",
    },
    margin: {
      l: 60,
      r: 30,
      t: 50,
      b: currentSelectedStructures.length > 0 ? 200 : 60,
    },
    hovermode: "closest" as const,
    shapes: [
      // Reference line at 50%
      {
        type: "line" as const,
        x0: 0,
        y0: 50,
        x1: 1,
        y1: 50,
        xref: "paper",
        yref: "y",
        line: {
          color: "rgba(128, 128, 128, 0.6)",
          width: 1,
          dash: "dot",
        },
      },
      // Reference line at 95%
      {
        type: "line" as const,
        x0: 0,
        y0: 95,
        x1: 1,
        y1: 95,
        xref: "paper",
        yref: "y",
        line: {
          color: "rgba(76, 175, 80, 0.6)",
          width: 1,
          dash: "dot",
        },
      },
    ],
  };

  // Plotly configuration
  const plotConfig: Partial<PlotConfig> = {
    responsive: true,
    displayModeBar: true,
    modeBarButtonsToRemove: ["select2d", "lasso2d", "autoScale2d"],
    displaylogo: false,
    modeBarButtonsToAdd: [
      {
        name: "Reset View",
        title: "Reset View",
        icon: {
          width: 1792,
          height: 1792,
          path: "M1664 896q0 156-61 298t-164 245-245 164-298 61q-172 0-327-72.5t-264-204.5q-7-10-6.5-22.5t8.5-20.5l137-138q10-9 25-10.5t23 6.5q166 165 389 165 120 0 227.5-46.5t187-125 125-187 46.5-227.5-46.5-227.5-125-187-187-125-227.5-46.5q-126 0-244.5 49t-204.5 136l280 280q10 10 10 25t-10 25l-92 92q-10 10-25 10t-25-10l-434-434q-10-10-10-25t10-25l92-92q10-10 25-10t25 10l280 280q139-139 321-139 156 0 298 61t245 164 164 245 61 298z",
          transform: "matrix(1 0 0 -1 0 1792)",
        },
        click: function (gd: PlotlyHTMLElement) {
          // Reset to original view
          if (gd && gd.layout && gd.layout.xaxis && gd.layout.yaxis) {
            gd.layout.xaxis.range = undefined as unknown as [number, number];
            gd.layout.yaxis.range = [0, 100];
            gd.layout.xaxis.autorange = true;
            gd.layout.yaxis.autorange = false;
          }

          // Use Plotly.relayout to update the plot
          if (typeof window !== "undefined") {
            const w = window as unknown as {
              Plotly?: {
                relayout: (
                  g: PlotlyHTMLElement,
                  u: Partial<PlotLayout>,
                ) => void;
              };
            };
            if (w.Plotly) {
              w.Plotly.relayout(gd, {
                "xaxis.autorange": true,
                "yaxis.range": [0, 100],
              });
            }
          }
        },
      },
    ],
    toImageButtonOptions: {
      format: "png" as const,
      filename: "dvh_chart",
      height: 600,
      width: 1000,
      scale: 1,
    },
  };

  // If no data available, return early (after hooks to preserve order)
  if (!currentDvhData) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>Loading DVH data...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Controls */}
      <Box
        sx={{
          mb: 0,
          display: "flex",
          gap: 2,
          alignItems: "flex-start",
          flexWrap: "wrap",
          minHeight: "56px",
        }}
      >
        {/* Structures Autocomplete */}
        <Autocomplete
          multiple
          size="medium"
          options={["Select All", ...availableStructures]}
          value={currentSelectedStructures}
          onChange={(_, newValue) => {
            // Filter out "Select All" from the selected values
            const filteredValues = newValue.filter(
              (value) => value !== "Select All",
            );
            handleStructuresChange(filteredValues);
          }}
          disableCloseOnSelect
          limitTags={3}
          componentsProps={{
            popper: {
              placement: "bottom-start",
              sx: {
                width: "300px !important",
                position: "fixed !important",
                overflow: "visible !important",
                "& .MuiAutocomplete-listbox": {
                  width: "100%",
                  maxHeight: "400px",
                  overflowY: "auto",
                },
                "& .MuiPaper-root": {
                  maxHeight: "none !important",
                  overflow: "visible !important",
                },
              },
              modifiers: [
                {
                  name: "flip",
                  enabled: false,
                },
                {
                  name: "preventOverflow",
                  enabled: false,
                },
                {
                  name: "hide",
                  enabled: false,
                },
              ],
              style: {
                zIndex: 10,
              },
            },
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                size="small"
              />
            ))
          }
          renderOption={(props, option, { selected }) => {
            // Special handling for "Select All" option
            if (option === "Select All") {
              const allSelected =
                currentSelectedStructures.length === availableStructures.length;
              const someSelected =
                currentSelectedStructures.length > 0 &&
                currentSelectedStructures.length < availableStructures.length;

              return (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    fontWeight: "bold",
                    height: "36px",
                    minHeight: "36px",
                    padding: "4px 16px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "white !important",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04) !important",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "white !important",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04) !important",
                    },
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (allSelected) {
                      handleStructuresChange([]);
                    } else {
                      handleStructuresChange(availableStructures);
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", flex: 1 }}
                  >
                    Select/Deselect All
                  </Typography>
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    sx={{ marginLeft: 1 }}
                  />
                </Box>
              );
            }

            return (
              <Box
                component="li"
                {...props}
                sx={{
                  height: "36px",
                  minHeight: "36px",
                  padding: "4px 16px",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "white !important",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04) !important",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "white !important",
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04) !important",
                  },
                }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {option}
                </Typography>
                <Checkbox
                  size="small"
                  checked={selected}
                  sx={{ marginLeft: 1 }}
                />
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Structures"
              placeholder={
                currentSelectedStructures.length === 0
                  ? "Select structures"
                  : ""
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                },
              }}
            />
          )}
          sx={{
            minWidth: 520,
            maxWidth: 520,
            zIndex: 10,
          }}
        />

        {showPlanFilters && (
          <RadioGroup
            row
            value={selectedPlanFilter}
            onChange={(e) => setSelectedPlanFilter(e.target.value)}
            sx={{ mt: "8px" }}
          >
            <FormControlLabel
              value="showAll"
              control={<Radio size="small" />}
              label="Both Plans"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
            />
            <FormControlLabel
              value="planA"
              control={<Radio size="small" />}
              label="Plan A"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
            />
            <FormControlLabel
              value="planB"
              control={<Radio size="small" />}
              label="Plan B"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
            />
          </RadioGroup>
        )}
      </Box>

      {/* Chart */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 650,
          position: "relative",
          "& .modebar": {
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "rgba(255, 255, 255, 0.95)",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            border: "1px solid rgba(0,0,0,0.08)",
            padding: "4px",
            gap: "2px",
            display: "flex",
            flexDirection: "row",
            zIndex: 5,
          },
          "& .modebar-btn": {
            width: "32px !important",
            height: "32px !important",
            borderRadius: "6px !important",
            border: "none !important",
            background: "transparent !important",
            cursor: "pointer !important",
            display: "flex !important",
            alignItems: "center !important",
            justifyContent: "center !important",
            transition: "all 0.2s ease !important",
            "&:hover": {
              background: "rgba(99, 102, 241, 0.1) !important",
              color: "#6366f1 !important",
            },
            "&.active": {
              background: "rgba(99, 102, 241, 0.15) !important",
              color: "#6366f1 !important",
            },
          },
          "& .modebar-btn svg": {
            width: "18px !important",
            height: "18px !important",
            fill: "#6b7280 !important",
          },
          "& .modebar-btn:hover svg": {
            fill: "#6366f1 !important",
          },
          "& .modebar-group": {
            display: "flex !important",
            gap: "2px !important",
            marginRight: "4px !important",
            "&:last-child": {
              marginRight: "0 !important",
            },
          },
        }}
      >
        {/* eslint-disable @typescript-eslint/no-explicit-any */}
        {React.createElement(Plot as any, {
          data: plotData,
          layout: plotLayout as any,
          config: plotConfig as any,
          style: { width: "100%", height: "100%" },
          useResizeHandler: true,
        })}
      </Box>

      {/* Chart Instructions */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
        Hover over curves for detailed values. Use toolbar to zoom, pan, or
        reset view. Reference lines at 50% and 95% volume.
      </Typography>
    </Box>
  );
};

export default DVHChart;
